/**
 * ProjectExplorer — VS Code-style file tree for Fix & Continue.
 *
 * Calls GET /editor/files to load the merged base+overlay tree.
 * Handles focused/full scope toggle, right-click context menu, stub creation.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { editorListFiles, editorReadFile, editorCreateFile, editorDeleteFile } from '../../services/workspaceService'
import { getAPIBaseUrl } from '../../environment.js'
import './ProjectExplorer.css'

// ── Tree building ──────────────────────────────────────────────────────────────

function buildTree(flatItems) {
  // Group files into a nested structure by path segments.
  // Returns { dirs: { name → subtree }, files: [item, ...] }
  const root = { dirs: {}, files: [] }

  for (const item of flatItems) {
    if (item.type === 'dir') continue  // dirs are derived, not explicit
    const parts = item.path.split('/')
    let node = root
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i]
      if (!node.dirs[seg]) node.dirs[seg] = { dirs: {}, files: [] }
      node = node.dirs[seg]
    }
    node.files.push(item)
  }

  return root
}

// ── Stub class content generation ─────────────────────────────────────────────

function generateStubContent(fqn) {
  const parts = fqn.split('.')
  const className = parts[parts.length - 1]
  const packageName = parts.slice(0, -1).join('.')
  const packageLine = packageName ? `package ${packageName};\n\n` : ''
  return `${packageLine}/**
 * COMPILE-ONLY STUB — generated by ModernizeIT
 * Replace with real implementation.
 */
public class ${className} {
    // Stub — no implementation
}
`
}

function fqnToPath(fqn) {
  return `src/main/java/${fqn.replace(/\./g, '/')}.java`
}

// ── Source badge ───────────────────────────────────────────────────────────────

function SourceBadge({ source }) {
  if (source === 'overlay') return <span className="pe-badge pe-badge--modified" title="Modified">M</span>
  if (source === 'created') return <span className="pe-badge pe-badge--created" title="Created by you">+</span>
  return null
}

// ── File icon ──────────────────────────────────────────────────────────────────

function FileIcon({ name }) {
  if (name === 'pom.xml') return <span className="pe-icon">📋</span>
  if (name.endsWith('.java')) return <span className="pe-icon">📄</span>
  return <span className="pe-icon">📄</span>
}

// ── Feature flag (easy rollback) ──────────────────────────────────────────────
const ENABLE_MODERNIZE_CONTEXT_MENU = true

// ── Context menu ───────────────────────────────────────────────────────────────
// ModernizeIT-specific context menu — intentionally replaces generic OS / VS Code
// actions (Reveal in Finder, Copy Path, etc.) that are irrelevant in a web-based
// virtual file system backed by S3 artifacts.

function ContextMenu({ x, y, item, onAction, onClose, hasJobContext, callbacks }) {
  const menuRef = useRef(null)

  useEffect(() => {
    const handle = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose()
    }
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', handle)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handle)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  // Adjust position so menu doesn't overflow viewport
  const [pos, setPos] = useState({ left: x, top: y })
  useEffect(() => {
    if (!menuRef.current) return
    const rect = menuRef.current.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    setPos({
      left: x + rect.width > vw ? Math.max(4, vw - rect.width - 8) : x,
      top:  y + rect.height > vh ? Math.max(4, vh - rect.height - 8) : y,
    })
  }, [x, y])

  // Context detection
  const isFolder = item?.type === 'dir'
  const isFile   = !isFolder
  const isJava   = isFile && (item?.path || '').endsWith('.java')
  const basename = (item?.path || '').split('/').pop() || 'item'

  // Fall back to old simple menu if feature flag is off
  if (!ENABLE_MODERNIZE_CONTEXT_MENU) {
    return (
      <div ref={menuRef} className="pe-context-menu" style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 9999 }}>
        <button onClick={() => { onAction('open', item); onClose() }}>Open</button>
        <button onClick={() => { onAction('new-file', item); onClose() }}>New File…</button>
        <button onClick={() => { onAction('generate-stub', item); onClose() }}>Generate Stub Class…</button>
        <div className="pe-context-separator" />
        <button className="pe-context-danger" onClick={() => { onAction('delete', item); onClose() }}>Delete</button>
      </div>
    )
  }

  return (
    <div ref={menuRef} className="pe-context-menu" style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 9999 }}>
      {/* Header */}
      <div className="pe-context-header">
        {isFolder ? '📁' : '📄'} {basename}
      </div>
      <div className="pe-context-separator" />

      {/* ── Section A: Project Actions ──────────────────────────────────── */}
      {hasJobContext && (
        <>
          <div className="pe-context-section">Project</div>
          <button
            onClick={() => { callbacks?.onRecompile?.(); onClose() }}
            disabled={!callbacks?.onRecompile}
          >
            🔄 Re-run Compile Check
          </button>
          <div className="pe-context-separator" />
        </>
      )}

      {/* ── Section B: File / Folder Actions ──────────────────────────── */}
      {isFile && (
        <button onClick={() => { onAction('open', item); onClose() }}>
          📂 Open in Editor
        </button>
      )}

      {/* ── Section C: Download Actions ─────────────────────────────────── */}
      <div className="pe-context-section">Download</div>
      {/* Download the right-clicked item — file content or folder zip */}
      <button
        onClick={() => { callbacks?.onDownload?.(item); onClose() }}
        disabled={!callbacks?.onDownload}
      >
        ⬇️ {isFolder ? 'Download Folder' : 'Download'}
      </button>
      {/* Download full project bundle — always available when job context exists */}
      {hasJobContext && (
        <button
          onClick={() => { callbacks?.onDownloadProject?.(); onClose() }}
          disabled={!callbacks?.onDownloadProject}
        >
          📦 Download Project
        </button>
      )}
      <div className="pe-context-separator" />

      {/* ── Section D: File Management ───────────────────────────────── */}
      <div className="pe-context-section">Manage</div>
      <button onClick={() => { onAction('new-file', item); onClose() }}>
        ✨ New File…
      </button>
      {isFile && (
        <button className="pe-context-danger" onClick={() => { onAction('delete', item); onClose() }}>
          🗑️ Delete
        </button>
      )}
    </div>
  )
}

// ── Stub dialog ────────────────────────────────────────────────────────────────

function StubDialog({ prefillFqn, onConfirm, onCancel }) {
  const [fqn, setFqn] = useState(prefillFqn || '')

  return (
    <div className="pe-dialog-overlay">
      <div className="pe-dialog">
        <div className="pe-dialog-title">Generate Stub Class</div>
        <div className="pe-dialog-body">
          <label>Fully qualified class name:</label>
          <input
            className="pe-dialog-input"
            value={fqn}
            onChange={e => setFqn(e.target.value)}
            placeholder="com.example.CustomerService"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter' && fqn.trim()) onConfirm(fqn.trim())
              if (e.key === 'Escape') onCancel()
            }}
          />
          <div className="pe-dialog-hint">
            Path: <code>{fqn.trim() ? fqnToPath(fqn.trim()) : '…'}</code>
          </div>
        </div>
        <div className="pe-dialog-footer">
          <button onClick={onCancel}>Cancel</button>
          <button
            className="pe-dialog-btn-primary"
            onClick={() => fqn.trim() && onConfirm(fqn.trim())}
            disabled={!fqn.trim()}
          >
            Generate
          </button>
        </div>
      </div>
    </div>
  )
}

// ── New-file dialog ────────────────────────────────────────────────────────────

function NewFileDialog({ onConfirm, onCancel }) {
  const [filename, setFilename] = useState('')

  return (
    <div className="pe-dialog-overlay">
      <div className="pe-dialog">
        <div className="pe-dialog-title">New File</div>
        <div className="pe-dialog-body">
          <label>File path (relative to project root):</label>
          <input
            className="pe-dialog-input"
            value={filename}
            onChange={e => setFilename(e.target.value)}
            placeholder="src/main/java/com/example/MyClass.java"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter' && filename.trim()) onConfirm(filename.trim())
              if (e.key === 'Escape') onCancel()
            }}
          />
        </div>
        <div className="pe-dialog-footer">
          <button onClick={onCancel}>Cancel</button>
          <button
            className="pe-dialog-btn-primary"
            onClick={() => filename.trim() && onConfirm(filename.trim())}
            disabled={!filename.trim()}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Delete confirm dialog ──────────────────────────────────────────────────────

function DeleteDialog({ path, onConfirm, onCancel }) {
  return (
    <div className="pe-dialog-overlay">
      <div className="pe-dialog">
        <div className="pe-dialog-title">Delete File</div>
        <div className="pe-dialog-body">
          <p>Delete <strong>{path.split('/').pop()}</strong>?</p>
          <p className="pe-dialog-warn">This file will no longer appear in the tree or be compiled.</p>
        </div>
        <div className="pe-dialog-footer">
          <button onClick={onCancel}>Cancel</button>
          <button className="pe-dialog-btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  )
}

// ── Tree node rendering ────────────────────────────────────────────────────────

function FolderNode({ name, fullPath, subtree, depth, onFileClick, onFileDoubleClick, onContextMenu, expandedDirs, toggleDir }) {
  const isExpanded = expandedDirs.has(fullPath)

  return (
    <div className="pe-folder">
      <div
        className="pe-folder-header"
        style={{ paddingLeft: depth * 12 + 4 }}
        onClick={() => toggleDir(fullPath)}
        onContextMenu={e => { e.preventDefault(); onContextMenu(e, { path: fullPath, type: 'dir' }) }}
      >
        <span className="pe-chevron">{isExpanded ? '▾' : '▸'}</span>
        <span className="pe-icon">📁</span>
        <span className="pe-folder-name">{name}</span>
      </div>
      {isExpanded && (
        <div className="pe-folder-children">
          <TreeLevel
            node={subtree}
            depth={depth + 1}
            onFileClick={onFileClick}
            onFileDoubleClick={onFileDoubleClick}
            onContextMenu={onContextMenu}
            expandedDirs={expandedDirs}
            toggleDir={toggleDir}
            pathPrefix={fullPath}
          />
        </div>
      )}
    </div>
  )
}

function TreeLevel({ node, depth, onFileClick, onFileDoubleClick, onContextMenu, expandedDirs, toggleDir, pathPrefix = '' }) {
  const dirNames = Object.keys(node.dirs).sort()
  const files = [...node.files].sort((a, b) => a.path.localeCompare(b.path))

  return (
    <div className="pe-tree-level">
      {dirNames.map(name => {
        const fullPath = pathPrefix ? `${pathPrefix}/${name}` : name
        return (
          <FolderNode
            key={fullPath}
            name={name}
            fullPath={fullPath}
            subtree={node.dirs[name]}
            depth={depth}
            onFileClick={onFileClick}
            onFileDoubleClick={onFileDoubleClick}
            onContextMenu={onContextMenu}
            expandedDirs={expandedDirs}
            toggleDir={toggleDir}
          />
        )
      })}
      {files.map(item => {
        const basename = item.path.split('/').pop()
        return (
          <div
            key={item.path}
            className="pe-file-row"
            style={{ paddingLeft: depth * 12 + 4 }}
            onClick={() => onFileClick(item)}
            onDoubleClick={() => onFileDoubleClick?.(item)}
            onContextMenu={e => { e.preventDefault(); onContextMenu(e, item) }}
          >
            <FileIcon name={basename} />
            <span className="pe-file-name">{basename}</span>
            <SourceBadge source={item.source} />
          </div>
        )
      })}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ProjectExplorer({
  gateJobId,
  refreshKey,         // increment to force reload from S3
  scope: externalScope,
  onScopeChange,
  onFileOpen,
  onFileDoubleClick,  // optional: called on double-click → opens as permanent tab
  activeFilePath,
  errorFiles,   // optional: basenames of files with compile errors, for smart auto-expand
  // ModernizeIT context menu callbacks (optional — menu items disabled if not provided)
  onRecompile,        // () => void — re-run compile check
  onAIResolveFile,    // (item) => void — AI resolve for a specific file
  onExplainIssue,     // (item) => void — explain top issue for a file
  onDownloadFile,     // (item) => void — download single file from S3
  onDownloadProject,  // () => void — download full project zip
}) {
  const [scope, setScope] = useState(externalScope || 'focused')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedDirs, setExpandedDirs] = useState(new Set())

  // Dialogs
  const [contextMenu, setContextMenu] = useState(null)   // { x, y, item }
  const [stubDialog, setStubDialog] = useState(null)     // { prefillFqn }
  const [newFileDialog, setNewFileDialog] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState(null)  // { path }
  const [working, setWorking] = useState(false)
  const [downloadMsg, setDownloadMsg] = useState(null)    // truthy string while downloading

  // ── Download helpers ──────────────────────────────────────────────────────

  // Trigger a browser save-as dialog from an in-memory blob
  const _triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Download a single file via GET /editor/file-download.
  // Uses the same bundle-aware resolution as folder-download:
  //   overlay first → project_bundle.zip fallback.
  // This avoids the flat-workspace 404s that editorReadFile causes for full-scope Maven paths.
  const _downloadFile = async (item) => {
    const apiUrl = getAPIBaseUrl()
    const token = localStorage.getItem('modernizeit_token')
    const headers = token ? { Authorization: `Bearer ${token}` } : {}

    const params = new URLSearchParams({ gate_job_id: gateJobId, file_path: item.path })
    const res = await fetch(`${apiUrl}/editor/file-download?${params}`, { headers })

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ detail: res.statusText }))
      throw new Error(errBody.detail || `File download failed: ${res.status}`)
    }

    const blob = await res.blob()
    _triggerBlobDownload(blob, item.path.split('/').pop())
  }

  // Download a folder as a zip via GET /editor/folder-download.
  // The backend reads project_bundle.zip directly (full Maven layout) so we avoid
  // the flat-workspace mismatch that caused editorReadFile to 404 for every file.
  const _downloadFolder = async (item) => {
    const folderPath = item.path.replace(/\/$/, '')  // strip any trailing slash

    // Quick guard: if no files are loaded under this folder, bail early
    const folderFiles = items.filter(i => i.type === 'file' && i.path.startsWith(folderPath + '/'))
    if (folderFiles.length === 0) return

    setDownloadMsg(`Downloading folder…`)

    const apiUrl = getAPIBaseUrl()
    const token = localStorage.getItem('modernizeit_token')
    const headers = token ? { Authorization: `Bearer ${token}` } : {}

    const params = new URLSearchParams({ gate_job_id: gateJobId, folder_path: folderPath })
    const res = await fetch(`${apiUrl}/editor/folder-download?${params}`, { headers })

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ detail: res.statusText }))
      throw new Error(errBody.detail || `Folder download failed: ${res.status}`)
    }

    const blob = await res.blob()
    const folderName = folderPath.split('/').pop() || 'folder'
    _triggerBlobDownload(blob, `${folderName}.zip`)
  }

  // Context-menu "Download" handler — dispatches to file or folder download
  const handleDownloadItem = useCallback(async (item) => {
    if (!gateJobId) return
    // Prefer external prop overrides if provided (backwards compat)
    if (item.type === 'file' && onDownloadFile) { onDownloadFile(item); return }
    setDownloadMsg('Downloading…')
    try {
      if (item.type === 'file') await _downloadFile(item)
      else if (item.type === 'dir') await _downloadFolder(item)
    } catch (err) {
      setError(err.message || 'Download failed')
    } finally {
      setDownloadMsg(null)
    }
  }, [gateJobId, items, onDownloadFile])  // eslint-disable-line react-hooks/exhaustive-deps

  // Context-menu "Download Project" handler — streams project bundle zip from API
  const handleDownloadProject = useCallback(() => {
    if (!gateJobId) return
    if (onDownloadProject) { onDownloadProject(); return }
    const apiUrl = getAPIBaseUrl()
    window.open(`${apiUrl}/artifacts/project-bundle/${gateJobId}`, '_blank')
  }, [gateJobId, onDownloadProject])

  const loadFiles = useCallback(async (s) => {
    if (!gateJobId) return
    setLoading(true)
    setError(null)
    try {
      const activeScope = s || scope
      const data = await editorListFiles(gateJobId, activeScope)
      setItems(data)
      // Auto-expand: for full scope, only expand folders on the path to error files.
      // For focused scope, expand everything (few files, all have errors).
      const dirs = new Set()
      const errorBasenames = new Set(
        (errorFiles || []).map(f => (f || '').split('/').pop()).filter(Boolean)
      )
      data.forEach(item => {
        if (item.type === 'file') {
          if (activeScope === 'full' && errorBasenames.size > 0) {
            const basename = item.path.split('/').pop()
            if (!errorBasenames.has(basename)) return
          }
          const parts = item.path.split('/')
          for (let i = 1; i < parts.length; i++) dirs.add(parts.slice(0, i).join('/'))
        }
      })
      setExpandedDirs(dirs)
    } catch (err) {
      setError(err.message || 'Could not load project files')
    } finally {
      setLoading(false)
    }
  }, [gateJobId, scope, errorFiles])

  useEffect(() => {
    loadFiles(scope)
  }, [gateJobId, scope, refreshKey])

  useEffect(() => {
    if (externalScope && externalScope !== scope) {
      setScope(externalScope)
    }
  }, [externalScope])

  const handleScopeChange = (newScope) => {
    setScope(newScope)
    onScopeChange?.(newScope)
    loadFiles(newScope)
  }

  const toggleDir = useCallback((name) => {
    setExpandedDirs(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }, [])

  const handleContextMenuAction = async (action, item) => {
    if (action === 'open') {
      onFileOpen?.(item)
    } else if (action === 'generate-stub') {
      setStubDialog({ prefillFqn: '' })
    } else if (action === 'new-file') {
      setNewFileDialog(true)
    } else if (action === 'delete') {
      setDeleteDialog({ path: item.path })
    }
  }

  const handleStubConfirm = async (fqn) => {
    setStubDialog(null)
    const path = fqnToPath(fqn)
    const content = generateStubContent(fqn)
    setWorking(true)
    try {
      await editorCreateFile(gateJobId, path, content, scope)
      await loadFiles(scope)
      // Open the new file in the editor
      onFileOpen?.({ path, type: 'file', source: 'created' })
    } catch (err) {
      setError(err.message || 'Failed to create stub file')
    } finally {
      setWorking(false)
    }
  }

  const handleNewFileConfirm = async (path) => {
    setNewFileDialog(false)
    setWorking(true)
    try {
      await editorCreateFile(gateJobId, path, `// New file: ${path}\n`, scope)
      await loadFiles(scope)
      onFileOpen?.({ path, type: 'file', source: 'created' })
    } catch (err) {
      setError(err.message || 'Failed to create file')
    } finally {
      setWorking(false)
    }
  }

  const handleDeleteConfirm = async () => {
    const path = deleteDialog.path
    setDeleteDialog(null)
    setWorking(true)
    try {
      await editorDeleteFile(gateJobId, path)
      await loadFiles(scope)
    } catch (err) {
      setError(err.message || 'Failed to delete file')
    } finally {
      setWorking(false)
    }
  }

  const tree = buildTree(items.filter(i => i.type === 'file'))
  const fileCount = items.filter(i => i.type === 'file').length
  const modifiedCount = items.filter(i => i.source === 'overlay' || i.source === 'created').length

  return (
    <div className="pe-root">
      {/* Toolbar */}
      <div className="pe-toolbar">
        <div className="pe-scope-tabs">
          <button
            className={`pe-scope-tab ${scope === 'focused' ? 'active' : ''}`}
            onClick={() => handleScopeChange('focused')}
          >
            Focused
          </button>
          <button
            className={`pe-scope-tab ${scope === 'full' ? 'active' : ''}`}
            onClick={() => handleScopeChange('full')}
          >
            Full Project
          </button>
        </div>
        <button
          className="pe-refresh-btn"
          onClick={() => loadFiles(scope)}
          title="Refresh file tree"
          disabled={loading}
        >
          ↺
        </button>
      </div>

      {/* Status line */}
      {!loading && !error && (
        <div className="pe-status">
          {fileCount} file{fileCount !== 1 ? 's' : ''}
          {modifiedCount > 0 && <span className="pe-status-modified"> · {modifiedCount} edited</span>}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="pe-loading">
          <div className="pe-spinner" />
          <span>Loading files…</span>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="pe-error">
          <div className="pe-error-msg">{error}</div>
          <button className="pe-retry-btn" onClick={() => loadFiles(scope)}>Retry</button>
        </div>
      )}

      {/* File tree */}
      {!loading && !error && (
        <div className="pe-tree">
          {fileCount === 0 ? (
            <div className="pe-empty">No files found</div>
          ) : (
            <TreeLevel
              node={tree}
              depth={0}
              onFileClick={(item) => onFileOpen?.(item)}
              onFileDoubleClick={(item) => onFileDoubleClick?.(item)}
              onContextMenu={(e, item) => setContextMenu({ x: e.clientX, y: e.clientY, item })}
              expandedDirs={expandedDirs}
              toggleDir={toggleDir}
              pathPrefix=""
            />
          )}
        </div>
      )}

      {/* Working overlay */}
      {working && <div className="pe-working">Saving…</div>}

      {/* Download overlay — shown while fetching file(s) for browser download */}
      {downloadMsg && <div className="pe-working">{downloadMsg}</div>}

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          item={contextMenu.item}
          onAction={handleContextMenuAction}
          onClose={() => setContextMenu(null)}
          hasJobContext={!!gateJobId}
          callbacks={{
            onRecompile,
            onAIResolveFile,
            onExplainIssue,
            onDownload: gateJobId ? handleDownloadItem : undefined,
            onDownloadProject: gateJobId ? handleDownloadProject : undefined,
          }}
        />
      )}

      {/* Stub dialog */}
      {stubDialog && (
        <StubDialog
          prefillFqn={stubDialog.prefillFqn}
          onConfirm={handleStubConfirm}
          onCancel={() => setStubDialog(null)}
        />
      )}

      {/* New file dialog */}
      {newFileDialog && (
        <NewFileDialog
          onConfirm={handleNewFileConfirm}
          onCancel={() => setNewFileDialog(false)}
        />
      )}

      {/* Delete confirm dialog */}
      {deleteDialog && (
        <DeleteDialog
          path={deleteDialog.path}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteDialog(null)}
        />
      )}
    </div>
  )
}
